/**
 * HmProductDetail — karta towaru z Symfonii: wszystkie parametry i edycja.
 *
 * Podgląd pokazuje KOMPLET tego, co Symfonia trzyma na karcie (52 pola), pogrupowany tak, jak
 * się tych rzeczy szuka. Edycja obejmuje świadomie WĘŻSZY zbiór — pola, które da się zmienić
 * bez wiedzy o konfiguracji firmy:
 *
 *  • KODU TOWARU nie edytujemy. `Products/Update` przy nieznanym kodzie TWORZY nowy towar zamiast
 *    odmówić, więc literówka nie kończyłaby się błędem, tylko śmieciem w kartotece.
 *  • Cen, jednostek, katalogu, rodzaju i danych logistycznych też nie — każde z nich ma w Symfonii
 *    własne reguły przeliczania i własną trasę; udawanie, że to zwykłe pola formularza, byłoby
 *    proszeniem się o cichy rozjazd danych.
 *
 * Zapis idzie do CUDZEGO systemu i jest nieodwracalny z naszej strony — stąd osobne uprawnienie
 * i przycisk, który mówi wprost, dokąd zapisuje.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { HM_PRICE_KINDS, type HmProductDetail as HmProductDetailDto } from '@saas/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { Pencil, Save, X } from 'lucide-react';

import { Button } from '@/components/Button';
import { Checkbox } from '@/components/Checkbox';
import {
  DetailError,
  DetailTabBody,
  DetailTabs,
  Field,
  Section,
  useDocumentTabLabel,
} from '@/components/handel-magazyn/detail-parts';
import { Input } from '@/components/Input';
import { useTenantAuth } from '@/contexts/TenantAuthContext';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';
import { useActiveCompanyScope } from '@/lib/use-active-company';

const TABS = ['basic', 'prices', 'units', 'logistics'] as const;
type Tab = (typeof TABS)[number];

const EDIT_PERMISSION = 'handel_magazyn.products.edit';

/** Pola, które wolno zmienić — reszta karty jest tylko do odczytu (patrz nagłówek pliku). */
interface EditState {
  name: string;
  active: boolean;
  vatRateCode: string;
  barcode: string;
  pkwiu: string;
  cn: string;
  fiscalName: string;
  deliveryScheme: string;
  note: string;
  minState: string;
  maxState: string;
  priceNegotiation: boolean;
  vat50Minus: boolean;
  reverseCharge: boolean;
  splitPayment: boolean;
}

function toEditState(product: HmProductDetailDto): EditState {
  return {
    name: product.name,
    active: product.active,
    vatRateCode: product.vatRateCode ?? '',
    barcode: product.barcode ?? '',
    pkwiu: product.pkwiu ?? '',
    cn: product.cn ?? '',
    fiscalName: product.fiscalName ?? '',
    deliveryScheme: product.deliveryScheme ?? '',
    note: product.note ?? '',
    minState: product.minState,
    maxState: product.maxState,
    priceNegotiation: product.priceNegotiation,
    vat50Minus: product.vat50Minus,
    reverseCharge: product.reverseCharge,
    splitPayment: product.splitPayment,
  };
}

/**
 * Wysyłamy WYŁĄCZNIE to, co się zmieniło. Nie dla oszczędności — trasa dokłada resztę karty
 * z odczytu, a krótsze żądanie znaczy mniej pól, które można przypadkiem nadpisać.
 */
function changedFields(before: EditState, after: EditState) {
  const patch: Record<string, string | number | boolean> = {};
  for (const key of Object.keys(after) as (keyof EditState)[]) {
    if (before[key] === after[key]) continue;
    if (key === 'minState' || key === 'maxState') patch[key] = Number(after[key]) || 0;
    else patch[key] = after[key];
  }
  return patch;
}

function yesNo(value: boolean, t: (key: string) => string): string {
  return value ? t('tenant.handelMagazyn.detail.yes') : t('tenant.handelMagazyn.detail.no');
}

export function HmProductDetail() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const companyId = useActiveCompanyScope().companyId;
  const toast = useToast();
  const queryClient = useQueryClient();
  const { me } = useTenantAuth();

  const [tab, setTab] = useState<Tab>('basic');
  const [edit, setEdit] = useState<EditState | null>(null);

  const canEdit =
    me?.user.isSuperUser === true || (me?.permissions.includes(EDIT_PERMISSION) ?? false);

  const query = useQuery({
    queryKey: ['handel-magazyn', 'product', id, companyId],
    enabled: !!companyId && !!id,
    retry: false,
    queryFn: async () =>
      (await api.get<HmProductDetailDto>(`/handel-magazyn/products/${id}?companyId=${companyId}`))
        .data,
  });

  const product = query.data;
  useDocumentTabLabel(`/handel-magazyn/towary/${id}`, product?.code);

  const mutation = useMutation({
    mutationFn: async (patch: Record<string, unknown>) =>
      (
        await api.put<HmProductDetailDto>(
          `/handel-magazyn/products/${id}?companyId=${companyId}`,
          patch,
        )
      ).data,
    onSuccess: (saved) => {
      queryClient.setQueryData(['handel-magazyn', 'product', id, companyId], saved);
      void queryClient.invalidateQueries({ queryKey: ['handel-magazyn', 'products'] });
      setEdit(null);
      toast.show({
        variant: 'success',
        title: t('tenant.handelMagazyn.products.toast.saved', { code: saved.code }),
      });
    },
    onError: (err) => {
      const data = isAxiosError(err)
        ? (err.response?.data as { message?: string; details?: { body?: string } } | undefined)
        : undefined;
      toast.show({
        variant: 'error',
        title: t('tenant.handelMagazyn.products.toast.failed'),
        description: data?.details?.body || data?.message || undefined,
      });
    },
  });

  if (query.isError) return <DetailError error={query.error} />;
  if (!product) {
    return <div className="p-4 text-sm text-text-secondary">{t('common.loading')}</div>;
  }

  const editing = edit !== null;
  const original = toEditState(product);
  const patch = editing ? changedFields(original, edit) : {};
  const dirty = Object.keys(patch).length > 0;

  return (
    <div className="flex h-full flex-col p-4">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-text-primary">
            {product.code} <span className="text-text-secondary">· {product.name}</span>
          </h1>
          <p className="text-sm text-text-secondary">
            {product.typeLabel}
            {!product.active && ` · ${t('tenant.handelMagazyn.products.inactive')}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {editing ? (
            <>
              <Button
                variant="secondary"
                size="sm"
                leftIcon={<X className="h-4 w-4" />}
                onClick={() => setEdit(null)}
                disabled={mutation.isPending}
              >
                {t('common.cancel')}
              </Button>
              <Button
                variant="primary"
                size="sm"
                leftIcon={<Save className="h-4 w-4" />}
                loading={mutation.isPending}
                disabled={!dirty}
                onClick={() => mutation.mutate(patch)}
                data-testid="hm-product-save"
              >
                {t('tenant.handelMagazyn.products.save')}
              </Button>
            </>
          ) : (
            canEdit && (
              <Button
                variant="secondary"
                size="sm"
                leftIcon={<Pencil className="h-4 w-4" />}
                onClick={() => setEdit(original)}
                data-testid="hm-product-edit"
              >
                {t('tenant.handelMagazyn.products.edit')}
              </Button>
            )
          )}
        </div>
      </div>

      {editing && (
        <div className="mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          {t('tenant.handelMagazyn.products.editHint')}
        </div>
      )}

      <DetailTabs tabs={TABS} active={tab} onChange={setTab} />
      <DetailTabBody>
        {tab === 'basic' &&
          (editing ? (
            <EditForm state={edit} onChange={(p) => setEdit({ ...edit, ...p })} />
          ) : (
            <BasicView product={product} yes={(v) => yesNo(v, t)} />
          ))}

        {tab === 'prices' && <PricesView product={product} />}
        {tab === 'units' && <UnitsView product={product} />}
        {tab === 'logistics' && <LogisticsView product={product} yes={(v) => yesNo(v, t)} />}
      </DetailTabBody>
    </div>
  );
}

/* ------------------------------------------------------------------ podglądy */

function BasicView({
  product,
  yes,
}: {
  product: HmProductDetailDto;
  yes: (value: boolean) => string;
}) {
  const { t } = useTranslation();
  return (
    <div className="grid gap-6 md:grid-cols-2">
      <Section title={t('tenant.handelMagazyn.products.sections.identity')}>
        <Field label={t('tenant.handelMagazyn.products.columns.code')} value={product.code} />
        <Field label={t('tenant.handelMagazyn.products.columns.name')} value={product.name} />
        <Field label={t('tenant.handelMagazyn.products.columns.type')} value={product.typeLabel} />
        <Field label={t('tenant.handelMagazyn.products.active')} value={yes(product.active)} />
        <Field label={t('tenant.handelMagazyn.products.kind')} value={product.kindCode} />
        <Field label={t('tenant.handelMagazyn.products.catalog')} value={product.catalogPath} />
        <Field label={t('tenant.handelMagazyn.products.fiscalName')} value={product.fiscalName} />
        <Field label={t('tenant.handelMagazyn.products.note')} value={product.note} />
      </Section>

      <Section title={t('tenant.handelMagazyn.products.sections.tax')}>
        <Field label={t('tenant.handelMagazyn.columns.vat')} value={product.vatRateCode} />
        <Field label={t('tenant.handelMagazyn.products.columns.pkwiu')} value={product.pkwiu} />
        <Field label={t('tenant.handelMagazyn.products.columns.cn')} value={product.cn} />
        <Field label={t('tenant.handelMagazyn.products.cnFull')} value={product.cnFull} />
        <Field
          label={t('tenant.handelMagazyn.products.jpkGroup')}
          value={product.jpkV7Group === 0 ? null : String(product.jpkV7Group)}
        />
        <Field label={t('tenant.handelMagazyn.products.vat50')} value={yes(product.vat50Minus)} />
        <Field
          label={t('tenant.handelMagazyn.products.reverseCharge')}
          value={yes(product.reverseCharge)}
        />
        <Field
          label={t('tenant.handelMagazyn.products.splitPayment')}
          value={yes(product.splitPayment)}
        />
      </Section>
    </div>
  );
}

function PricesView({ product }: { product: HmProductDetailDto }) {
  const { t } = useTranslation();
  const money = (m: { value: string; currency: string } | null) =>
    m ? `${m.value} ${m.currency}` : null;
  return (
    <div className="grid gap-6 md:grid-cols-2">
      <Section title={t('tenant.handelMagazyn.products.sections.basePrices')}>
        <Field
          label={t('tenant.handelMagazyn.products.basePrice')}
          value={money(product.basePrice)}
        />
        <Field
          label={t('tenant.handelMagazyn.products.purchasePrice')}
          value={money(product.purchasePrice)}
        />
        <Field
          label={t('tenant.handelMagazyn.products.purchasePriceCurrency')}
          value={money(product.purchasePriceInCurrency)}
        />
        <Field
          label={t('tenant.handelMagazyn.products.priceNegotiation')}
          value={product.priceNegotiation ? '✓' : '—'}
        />
        <Field
          label={t('tenant.handelMagazyn.products.currencyMode')}
          value={
            product.currencyMode ? `${product.currencyRate} × ${product.currencyConversion}` : '—'
          }
        />
      </Section>

      <Section title={t('tenant.handelMagazyn.products.sections.salePrices')}>
        {product.prices.length === 0 ? (
          <p className="text-sm text-text-tertiary">
            {t('tenant.handelMagazyn.products.noPrices')}
          </p>
        ) : (
          <div className="space-y-1">
            {product.prices.map((price, index) => (
              <Field
                key={`${price.priceType}-${index}`}
                label={`${t('tenant.handelMagazyn.products.priceType')} ${price.priceType}`}
                // Rodzaj ceny: 1 = brutto, 2 = netto (pułapka kontraktu, patrz DTO).
                value={`${price.value} ${price.currency} ${
                  price.kind === HM_PRICE_KINDS.GROSS
                    ? t('tenant.handelMagazyn.newSale.priceKindGross')
                    : t('tenant.handelMagazyn.newSale.priceKindNet')
                }`}
              />
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}

function UnitsView({ product }: { product: HmProductDetailDto }) {
  const { t } = useTranslation();
  return (
    <div className="grid gap-6 md:grid-cols-2">
      <Section title={t('tenant.handelMagazyn.products.sections.units')}>
        <Field label={t('tenant.handelMagazyn.products.recordUnit')} value={product.recordUnit} />
        <Field label={t('tenant.handelMagazyn.products.defaultUnit')} value={product.defaultUnit} />
        <Field label={t('tenant.handelMagazyn.products.displayUnit')} value={product.displayUnit} />
        <Field
          label={t('tenant.handelMagazyn.products.additionalUnit1')}
          value={
            product.additionalUnit1
              ? `${product.additionalUnit1} (× ${product.ratioOfAdditionalUnit1})`
              : null
          }
        />
        <Field
          label={t('tenant.handelMagazyn.products.additionalUnit2')}
          value={
            product.additionalUnit2
              ? `${product.additionalUnit2} (× ${product.ratioOfAdditionalUnit2})`
              : null
          }
        />
      </Section>

      <Section title={t('tenant.handelMagazyn.products.sections.stock')}>
        <Field label={t('tenant.handelMagazyn.products.minState')} value={product.minState} />
        <Field label={t('tenant.handelMagazyn.products.maxState')} value={product.maxState} />
        <Field
          label={t('tenant.handelMagazyn.products.deliveryScheme')}
          value={product.deliveryScheme}
        />
      </Section>
    </div>
  );
}

function LogisticsView({
  product,
  yes,
}: {
  product: HmProductDetailDto;
  yes: (value: boolean) => string;
}) {
  const { t } = useTranslation();
  return (
    <div className="grid gap-6 md:grid-cols-2">
      <Section title={t('tenant.handelMagazyn.products.sections.barcodes')}>
        <Field label={t('tenant.handelMagazyn.products.columns.barcode')} value={product.barcode} />
        {product.barcodes.map((b, index) => (
          <Field
            key={`${b.barcode}-${index}`}
            label={b.unit ?? t('tenant.handelMagazyn.products.columns.barcode')}
            value={b.barcode}
          />
        ))}
      </Section>

      <Section title={t('tenant.handelMagazyn.products.sections.other')}>
        <Field label={t('tenant.handelMagazyn.products.fkId')} value={product.fkId} />
        <Field label={t('tenant.handelMagazyn.products.fkParameter')} value={product.fkParameter} />
        <Field
          label={t('tenant.handelMagazyn.products.intrastatUnit')}
          value={product.intrastatUnit}
        />
        <Field
          label={t('tenant.handelMagazyn.products.staticSet')}
          value={yes(product.staticSet)}
        />
        {product.logisticFields.map((f, index) => (
          <Field key={`${f.label}-${index}`} label={f.label} value={f.value} />
        ))}
      </Section>
    </div>
  );
}

/* -------------------------------------------------------------------- edycja */

function EditForm({
  state,
  onChange,
}: {
  state: EditState;
  onChange: (patch: Partial<EditState>) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="grid gap-6 md:grid-cols-2">
      <Section title={t('tenant.handelMagazyn.products.sections.identity')}>
        <Input
          label={t('tenant.handelMagazyn.products.columns.name')}
          value={state.name}
          onChange={(e) => onChange({ name: e.target.value })}
          data-testid="hm-product-name"
        />
        <Input
          label={t('tenant.handelMagazyn.products.fiscalName')}
          value={state.fiscalName}
          onChange={(e) => onChange({ fiscalName: e.target.value })}
        />
        <Input
          label={t('tenant.handelMagazyn.products.note')}
          value={state.note}
          onChange={(e) => onChange({ note: e.target.value })}
        />
        <Checkbox
          checked={state.active}
          onChange={(e) => onChange({ active: e.target.checked })}
          label={t('tenant.handelMagazyn.products.active')}
        />
      </Section>

      <Section title={t('tenant.handelMagazyn.products.sections.tax')}>
        <Input
          label={t('tenant.handelMagazyn.columns.vat')}
          value={state.vatRateCode}
          onChange={(e) => onChange({ vatRateCode: e.target.value })}
          hint={t('tenant.handelMagazyn.products.vatHint')}
          data-testid="hm-product-vat"
        />
        <Input
          label={t('tenant.handelMagazyn.products.columns.pkwiu')}
          value={state.pkwiu}
          onChange={(e) => onChange({ pkwiu: e.target.value })}
        />
        <Input
          label={t('tenant.handelMagazyn.products.columns.cn')}
          value={state.cn}
          onChange={(e) => onChange({ cn: e.target.value })}
        />
        <Input
          label={t('tenant.handelMagazyn.products.columns.barcode')}
          value={state.barcode}
          onChange={(e) => onChange({ barcode: e.target.value })}
        />
        <div className="grid grid-cols-2 gap-2">
          <Input
            label={t('tenant.handelMagazyn.products.minState')}
            value={state.minState}
            onChange={(e) => onChange({ minState: e.target.value })}
            inputMode="decimal"
          />
          <Input
            label={t('tenant.handelMagazyn.products.maxState')}
            value={state.maxState}
            onChange={(e) => onChange({ maxState: e.target.value })}
            inputMode="decimal"
          />
        </div>
        <Checkbox
          checked={state.vat50Minus}
          onChange={(e) => onChange({ vat50Minus: e.target.checked })}
          label={t('tenant.handelMagazyn.products.vat50')}
        />
        <Checkbox
          checked={state.reverseCharge}
          onChange={(e) => onChange({ reverseCharge: e.target.checked })}
          label={t('tenant.handelMagazyn.products.reverseCharge')}
        />
        <Checkbox
          checked={state.splitPayment}
          onChange={(e) => onChange({ splitPayment: e.target.checked })}
          label={t('tenant.handelMagazyn.products.splitPayment')}
        />
        <Checkbox
          checked={state.priceNegotiation}
          onChange={(e) => onChange({ priceNegotiation: e.target.checked })}
          label={t('tenant.handelMagazyn.products.priceNegotiation')}
        />
      </Section>
    </div>
  );
}

export default HmProductDetail;
